import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useNotifier } from '@provider/notifier';
import { useUserIdentityState } from '@provider/user-identity';
import { courseToSimilarityKey } from '../utils/course-similarity.js';
import { getAllCoursesQuery } from './query.get-all.js';
import { updateCourseRequest } from './request.update-course.js';

function useUpdateCourseMutation() {
  const client = useQueryClient();
  const notifier = useNotifier();
  const user = useUserIdentityState(state => state.user);
  return useMutation(updateCourseMutation(client, notifier, user));
}

/**
 * Updates a course optimisticly and reverts in case anything fails on the
 * backend.
 */
const updateCourseMutation = (client, notifier, user) => {
  return {
    mutationKey: ["course-builder/update", user?.id],
    mutationFn: ({
      oldCourse,
      newCourse,
      options
    }) => {
      return updateCourseRequest(notifier, newCourse, user, {
        similarityKey: courseToSimilarityKey(oldCourse ?? newCourse),
        includeBlocks: options?.includeBlocks
      });
    },
    onMutate: async input => {
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), previous => previous?.map(it => it.id === input.newCourse.id ? input.newCourse : it));
      await client.cancelQueries({
        queryKey: getAllCoursesQuery.queryKey({
          user
        })
      });
      return {
        optimisticCourse: input.newCourse
      };
    },
    onSuccess: (result, input) => {
      if (!input.options?.silent) {
        notifier.notify({
          id: "course-builder.update",
          type: "success",
          body: "Forløbet er blevet opdateret."
        });
      }
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), previous => previous?.map(it => it.id === result.id ? result : it));
    },
    onError: (error, input, context) => {
      notifier.notify({
        id: "course-builder.update.error",
        type: "error",
        body: "Forløbet kunne ikke opdateres. Prøv igen om lidt."
      });
      client.setQueryData(getAllCoursesQuery.queryKey({
        user
      }), previous => previous?.map(course => course.id === context?.optimisticCourse.id ? input.oldCourse : course));
    }
  };
};

export { useUpdateCourseMutation };
//# sourceMappingURL=mutation.update.js.map
